import { forceSimulation } from 'd3-force';
import * as go from 'gojs';

import { defaultD3ForceDirectedLayoutSettings } from 'src/containers/ProjectDiagram/templates/extensions/layouts/constants';
import {
  D3LayoutLink,
  D3LayoutModel,
  D3LayoutNode,
} from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/types';
import { connectSubgraphs } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/connectSubgraphs';
import { forceCollideSimulation } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/forceCollideSimulation';
import { forceLinkSimulation } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/forceLinkSimulation';
import { forceManyBodySimulation } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/forceManyBodySimulation';
import { getSubgraphs } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/getSubgraphs';
import { toGoJSKey } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/keys';
import { mapToLayoutModel } from 'src/containers/ProjectDiagram/templates/extensions/layouts/ForceDirectedLayout/utils/mapToLayoutModel';
import { ForceLayoutSettings } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';

export class ExtendedForceDirectedLayout extends go.Layout {
  layoutSettings: ForceLayoutSettings;

  constructor({
    layoutSettings,
  }: {
    layoutSettings?: Partial<ForceLayoutSettings>;
  }) {
    super();

    this.layoutSettings = {
      ...defaultD3ForceDirectedLayoutSettings,
      ...(layoutSettings || {}),
    };
  }

  cloneProtected(copy: this) {
    super.cloneProtected(this);
    // eslint-disable-next-line no-param-reassign
    copy.layoutSettings = { ...this.layoutSettings };
  }

  doLayout(coll: go.Diagram | go.Group | go.Iterable<go.Part>) {
    if (!this.diagram) {
      return;
    }

    const parts = this.collectParts(coll);
    // TODO remove filtering out link labels when solution to colliding them with nodes is found
    const nodes = parts.filter(
      (part: go.Part) => part instanceof go.Node && !part.data.isLinkLabel
    ) as go.Set<go.Node>;
    const links = parts.filter(
      (part: go.Part) => part instanceof go.Link
    ) as go.Set<go.Link>;

    const subgraphLinks = connectSubgraphs(getSubgraphs(nodes));

    // Convert the go.js graph to the shape that d3's simulation expects
    const d3Graph = mapToLayoutModel(
      this.layoutSettings,
      nodes,
      links,
      subgraphLinks,
      (link) =>
        (link.data.labelKeys || [])
          .map(
            (label: string) =>
              (this.diagram
                ?.findNodeForKey(label)
                ?.findObject('LABEL TEXT') as go.TextBlock) || null
          )
          .filter((x: go.TextBlock | null) => x != null)
          .pop()
    );

    this.runSimulation(d3Graph);

    // Apply the new node positions
    d3Graph.nodes.forEach(({ key, x, y }) => {
      const diagramNode = this.diagram?.findNodeForKey(toGoJSKey(key));
      if (diagramNode) {
        diagramNode.move(new go.Point(x, y));
      }
    });
  }

  runSimulation({ nodes, links }: D3LayoutModel) {
    const simulation = forceSimulation<D3LayoutNode, D3LayoutLink>()
      .alpha(this.layoutSettings.layoutSimulationAlpha)
      .nodes(nodes)
      .force(
        'link',
        this.layoutSettings.enableLinkForce
          ? forceLinkSimulation(this.layoutSettings, links).links(links)
          : null
      )
      .force(
        'charge',
        this.layoutSettings.enableChargeForce
          ? forceManyBodySimulation(this.layoutSettings)
              .distanceMax(this.layoutSettings.layoutForceMaxCharge)
              .distanceMin(this.layoutSettings.layoutForceMinCharge)
          : null
      )
      .force(
        'collide',
        this.layoutSettings.enableCollisionForce
          ? forceCollideSimulation(this.layoutSettings)
          : null
      );

    // Perform the simulation manually
    for (
      let i = 0,
        n = Math.ceil(
          Math.log(simulation.alphaMin()) /
            Math.log(1 - simulation.alphaDecay())
        );
      i < n;
      i += 1
    ) {
      simulation.tick();
    }
  }
}
